import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { BaseWooCommerceV2ApiService } from '../woo-commerce/api/base-woo-commerce-v2-api.service';

export interface ApplePayConfig {
  merchantId: string;
  merchantName: string;
  supportedNetworks: string[];
  merchantCapabilities: string[];
  enabled: boolean;
}

export interface ApplePayPaymentRequest {
  countryCode: string;
  currencyCode: string;
  total: {
    label: string;
    amount: string;
    type?: 'final' | 'pending';
  };
  supportedNetworks?: string[];
  merchantCapabilities?: string[];
  requiredBillingContactFields?: string[];
  requiredShippingContactFields?: string[];
}

export interface ApplePayTokenResult {
  token: any;
  billingContact?: any;
  shippingContact?: any;
}

export interface ApplePayChargeRequest {
  order_id: number;
  payment_token: any;
}

export interface ApplePayChargeResponse {
  success: boolean;
  transaction_id?: string;
  [key: string]: any;
}

export interface ApplePayMerchantValidationRequest {
  validationURL: string;
}

export class ApplePayChargeError extends Error {
  constructor(
    public readonly orderId: number,
    public readonly originalError?: unknown
  ) {
    super(`Apple Pay charge failed for order #${orderId}`);
    this.name = 'ApplePayChargeError';
  }
}

/**
 * Declare the global ApplePaySession type for TypeScript.
 * This is available natively in Safari on macOS/iOS.
 */
declare class ApplePaySession {
  static readonly STATUS_SUCCESS: number;
  static readonly STATUS_FAILURE: number;
  static canMakePayments(): boolean;
  static supportsVersion(version: number): boolean;

  constructor(version: number, paymentRequest: any);

  begin(): void;
  abort(): void;
  completeMerchantValidation(merchantSession: any): void;
  completePayment(result: { status: number }): void;

  onvalidatemerchant: ((event: any) => void) | null;
  onpaymentauthorized: ((event: any) => void) | null;
  oncancel: ((event: any) => void) | null;
  onpaymentmethodselected: ((event: any) => void) | null;
}

@Injectable({
  providedIn: 'root'
})
export class ApplePayService extends BaseWooCommerceV2ApiService {
  private readonly validateMerchantEndpoint =
    '/payments/apple-pay/validate-merchant';
  private readonly processPaymentEndpoint =
    '/payments/apple-pay/process-payment';

  constructor(httpClient: HttpClient) {
    super(httpClient);
  }

  /**
   * Check if Apple Pay configuration is present in the environment
   */
  public get isConfigured(): boolean {
    const config = this.getConfig();
    return !!config?.enabled && !!config?.merchantId;
  }

  /**
   * Check if the current device/browser supports Apple Pay
   */
  public isAvailable(): boolean {
    if (!this.isConfigured) {
      return false;
    }

    try {
      return (
        typeof ApplePaySession !== 'undefined' &&
        ApplePaySession.canMakePayments()
      );
    } catch {
      return false;
    }
  }

  /**
   * Start an Apple Pay session with the given payment request parameters.
   * Returns a promise that resolves with the encrypted Apple Pay token,
   * or rejects if the user cancels or an error occurs.
   */
  public startSession(
    paymentRequest: ApplePayPaymentRequest
  ): Promise<ApplePayTokenResult> {
    return new Promise((resolve, reject) => {
      const config = this.getConfig();
      if (!config) {
        reject(new Error('Apple Pay is not configured in the environment.'));
        return;
      }

      const request = {
        countryCode: paymentRequest.countryCode,
        currencyCode: paymentRequest.currencyCode,
        supportedNetworks:
          paymentRequest.supportedNetworks || config.supportedNetworks,
        merchantCapabilities:
          paymentRequest.merchantCapabilities || config.merchantCapabilities,
        total: paymentRequest.total,
        requiredBillingContactFields:
          paymentRequest.requiredBillingContactFields || [
            'postalAddress',
            'name',
            'email',
            'phone'
          ],
        requiredShippingContactFields:
          paymentRequest.requiredShippingContactFields || []
      };

      let session: ApplePaySession;
      try {
        // Use Apple Pay JS API version 3
        session = new ApplePaySession(3, request);
      } catch (err) {
        reject(
          new Error(
            'Failed to create Apple Pay session. ' +
              (err instanceof Error ? err.message : String(err))
          )
        );
        return;
      }

      // Handle merchant validation: Apple sends a validationURL that our backend
      // must call with the Merchant Identity Certificate to get a merchant session.
      session.onvalidatemerchant = (event: any) => {
        const validationURL: string = event.validationURL;

        this.validateMerchant(validationURL).subscribe({
          next: (merchantSession) => {
            session.completeMerchantValidation(merchantSession);
          },
          error: (err) => {
            console.error(
              '[ApplePay] Merchant validation failed:',
              err
            );
            session.abort();
            reject(
              new Error('Apple Pay merchant validation failed. Please try again.')
            );
          }
        });
      };

      // Handle payment authorization: the user has authenticated (Face ID / Touch ID)
      // and Apple provides the encrypted payment token.
      session.onpaymentauthorized = (event: any) => {
        const payment = event.payment;

        // Complete the payment sheet with success
        session.completePayment({
          status: ApplePaySession.STATUS_SUCCESS
        });

        resolve({
          token: payment.token,
          billingContact: payment.billingContact,
          shippingContact: payment.shippingContact
        });
      };

      // Handle user cancellation
      session.oncancel = () => {
        reject(new Error('Apple Pay payment was cancelled by the user.'));
      };

      // Begin the session -- this shows the Apple Pay payment sheet
      session.begin();
    });
  }

  /**
   * Call the backend to validate the merchant with Apple Pay servers.
   */
  public validateMerchant(validationURL: string): Observable<any> {
    return this.post<any>(this.validateMerchantEndpoint, { validationURL });
  }

  /**
   * Call the backend to process the Apple Pay payment through Converge.
   */
  public processPayment(
    payload: ApplePayChargeRequest
  ): Observable<ApplePayChargeResponse> {
    return this.post<ApplePayChargeResponse>(
      this.processPaymentEndpoint,
      payload
    );
  }

  /**
   * Charge an order using the Apple Pay token via the backend.
   */
  public chargeOrder(
    orderId: number,
    token: any
  ): Observable<ApplePayChargeResponse> {
    return this.processPayment({
      order_id: orderId,
      payment_token: token
    }).pipe(
      catchError((err) => {
        console.error('[ApplePay] Charge failed:', err);
        return throwError(() => new ApplePayChargeError(orderId, err));
      })
    );
  }

  /**
   * Get Apple Pay config from environment.
   */
  private getConfig(): ApplePayConfig | undefined {
    return (environment as any)?.applePay as ApplePayConfig | undefined;
  }
}
